import { Wrench, Clock, Stack, Info, ArrowRight } from "@phosphor-icons/react";
import { fmtEur } from "../../lib/api";
import { t, pickName } from "../../lib/i18n";
import { track } from "../../lib/analytics";
import { useBooking, STEP_ADDONS } from "./BookingContext";

/**
 * Catálogo de servicios — Fase 1 redesign premium.
 * Cards anchos: icono dorado · nombre · descripción · duración · extras · precio dorado destacado a la derecha.
 */
export default function ServicioCard() {
  const { data, locale, setService, setAddonQty, setStep, operator, slug } = useBooking();
  const tr = (k, params) => t(k, locale, params);
  const catalog = data?.catalog || [];

  return (
    <>
      {operator && <p className="text-xs uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">{operator.name}</p>}
      <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tighter mt-2 text-[#1A1A1A] dark:text-white">
        {tr("que.necesita")}
      </h2>
      <p className="text-xs sm:text-sm mt-2 text-gray-500 dark:text-gray-400">
        Toca un servicio para añadir extras y elegir hora.
      </p>

      <div className="grid grid-cols-1 gap-3 mt-6 sm:mt-8" data-testid="services-grid">
        {catalog.map((c) => {
          const priceLabel = c.requires_inspection || (c.price_min != null && c.price_max != null)
            ? `${tr("desde") || "Desde"} ${fmtEur(c.price_min ?? c.price)}`
            : fmtEur(c.price);
          const durLabel = c.duration_days
            ? `${c.duration_days} ${c.duration_days === 1 ? "día" : "días"}`
            : `${c.duration_min || 30} ${tr("min")}`;
          return (
            <button
              key={c.id}
              onClick={() => {
                setService(c); setAddonQty({}); setStep(STEP_ADDONS);
                track("booking_start", { orgSlug: slug, orgId: data?.org?.id, metadata: { service: c.name } });
              }}
              className="group text-left rounded-2xl p-4 sm:p-5 border transition-all active:scale-[0.99] hover:-translate-y-0.5
                bg-white border-gray-200 hover:border-[#D4AF37] hover:shadow-lg hover:shadow-amber-900/10
                dark:bg-[#1A1D26] dark:border-white/10 dark:hover:border-[#F5C76A] dark:hover:shadow-amber-900/20"
              data-testid={`pub-service-${c.id}`}
            >
              <div className="flex items-stretch gap-4">
                {/* Icono dorado izquierdo */}
                <div className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl grid place-items-center
                  bg-gradient-to-br from-[#FAF0D7] to-[#F5E1A4]
                  dark:from-[#2A2A1F] dark:to-[#3A2E0E]">
                  <Wrench size={22} weight="duotone" className="text-[#B8860B] dark:text-[#F5C76A]" />
                </div>

                {/* Contenido principal */}
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-base sm:text-lg leading-tight text-[#1A1A1A] dark:text-white">
                    {pickName(c, locale)}
                  </div>
                  {c.description && (
                    <div className="text-xs sm:text-sm mt-1 text-gray-500 dark:text-gray-400 line-clamp-2">
                      {c.description}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs tabular-nums">
                    <span className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                      <Clock size={12} weight="fill" className="text-[var(--ag-text-muted)] dark:text-gray-500" />
                      {durLabel}
                    </span>
                    {!!(c.addons || []).length && (
                      <span className="inline-flex items-center gap-1 font-semibold text-[#B8860B] dark:text-[#F5C76A]">
                        <Stack size={12} weight="fill" />
                        {(c.addons || []).length} {tr("paso.complementos").toLowerCase()}
                      </span>
                    )}
                    {c.requires_inspection && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold
                        bg-amber-100 text-amber-900
                        dark:bg-amber-500/10 dark:text-amber-300"
                        data-testid={`pub-service-inspection-${c.id}`}
                      >
                        <Info size={10} weight="fill" /> Requiere inspección
                      </span>
                    )}
                  </div>
                </div>

                {/* Precio destacado dorado a la derecha */}
                <div className="shrink-0 flex flex-col items-end justify-between">
                  <div className="font-display font-black text-2xl sm:text-3xl tabular-nums tracking-tight
                    text-[#B8860B] dark:text-[#F5C76A]"
                    data-testid={`pub-service-price-${c.id}`}>
                    {priceLabel}
                  </div>
                  <div className="hidden sm:flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold mt-2
                    text-[var(--ag-text-muted)] group-hover:text-[#B8860B]
                    dark:text-gray-500 dark:group-hover:text-[#F5C76A] transition-colors">
                    Reservar <ArrowRight size={11} weight="bold" />
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}
